import React, { useState, useMemo } from "react";
import { toast } from "sonner";
import { filterAndSort, sortArr, formatDate, statusMedico, statusPRL } from "@/lib/helpers";
import { exportExcel, exportPDF } from "@/lib/exports";
import PaginationBar from "@/components/PaginationBar";
import DatePicker from "@/components/DatePicker";

const blank = {
  nombre: "", dni: "", telefono: "", email: "",
  fechaReconocimientoMedico: "", fechaCaducidadPRL: "",
  vehiculo: "", matricula: "", etiquetaAmbiental: "",
  acuerdos: "", herramientaPrestada: "",
};

const ETIQUETAS = ["", "0 (A)", "ECO", "C", "B", "Sin etiqueta"];

export default function Personal({ state, upsert, remove }) {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ field: "nombre", dir: 1 });
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(blank);
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(25);

  const toggleSort = (field) =>
    setSort((s) => (s.field === field ? { field, dir: -s.dir } : { field, dir: 1 }));

  const allRows = useMemo(
    () => filterAndSort(state.personal, search, sort, state),
    [state.personal, search, sort, state]
  );
  const pagedRows = allRows.slice(skip, skip + limit);

  const openModal = (row = null) => {
    setForm(row ? { ...blank, ...row } : { ...blank });
    setModal(true);
  };

  const save = async (e) => {
    e.preventDefault();
    await upsert("personal", form);
    toast.success("Guardado");
    setModal(false);
  };

  const cols = [
    { k: "nombre", label: "Nombre" },
    { k: "dni", label: "DNI" },
    { k: "telefono", label: "Tlf." },
    { k: "email", label: "Email" },
    { k: "statusMedico", label: "Reconoc. médico" },
    { k: "statusPRL", label: "PRL" },
    { k: "vehiculo", label: "Vehículo" },
    { k: "matricula", label: "Matrícula" },
    { k: "etiquetaAmbiental", label: "Etiq. medioamb." },
  ];

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap gap-2 items-center">
        <input className="input max-w-xs" placeholder="Buscar personal..."
               value={search} onChange={(e) => setSearch(e.target.value)}
               data-testid="search-personal"/>
        <div className="flex-1"/>
        <button className="btn btn-ghost" onClick={() => exportExcel("personal", state) || toast.error("Sin datos")}>Excel</button>
        <button className="btn btn-ghost" onClick={() => exportPDF("personal", state) || toast.error("Sin datos")}>PDF</button>
        <button className="btn btn-primary" onClick={() => openModal()} data-testid="btn-new-personal">+ Nuevo personal</button>
      </div>

      <div className="card overflow-hidden">
        <table className="data">
          <thead><tr>
            {cols.map((c) => (
              <th key={c.k} className={`sortable ${sort.field === c.k ? "active" : ""}`} onClick={() => toggleSort(c.k)}>
                {c.label} <span className="arr">{sortArr(sort, c.k)}</span>
              </th>
            ))}
            <th/>
          </tr></thead>
          <tbody>
            {pagedRows.map((p) => {
              const sm = statusMedico(p);
              const sp = statusPRL(p);
              return (
                <tr key={p.id}>
                  <td className="font-semibold">{p.nombre}</td>
                  <td>{p.dni}</td>
                  <td>{p.telefono}</td>
                  <td>{p.email}</td>
                  <td>
                    <div>{formatDate(p.fechaReconocimientoMedico)}</div>
                    {p.fechaReconocimientoMedico && <span className={`badge ${sm.cls}`}>{sm.txt}</span>}
                  </td>
                  <td>
                    <div>{formatDate(p.fechaCaducidadPRL)}</div>
                    {p.fechaCaducidadPRL && <span className={`badge ${sp.cls}`}>{sp.txt}</span>}
                  </td>
                  <td>{p.vehiculo}</td>
                  <td>{p.matricula}</td>
                  <td>{p.etiquetaAmbiental}</td>
                  <td className="text-right whitespace-nowrap">
                    <button className="btn btn-ghost !py-1 !px-2" onClick={() => openModal(p)}>Editar</button>
                    <button className="btn btn-danger !py-1 !px-2" onClick={() => remove("personal", p.id)}>✕</button>
                  </td>
                </tr>
              );
            })}
            {allRows.length === 0 && (
              <tr><td colSpan="10" className="text-center text-slate-400 py-6">No hay personal registrado</td></tr>
            )}
          </tbody>
        </table>
        <PaginationBar total={allRows.length} skip={skip} limit={limit}
                       onSkipChange={setSkip} onLimitChange={setLimit}/>
      </div>

      {modal && (
        <div className="modal-backdrop">
          <div className="modal p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg">{form.id ? "Editar" : "Nuevo"} personal</h3>
              <button onClick={() => setModal(false)} className="text-slate-400 hover:text-slate-700 text-2xl">✕</button>
            </div>
            <form onSubmit={save} className="grid md:grid-cols-2 gap-4">
              <div><label className="label">Nombre *</label>
                <input className="input" required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="personal-nombre"/></div>
              <div><label className="label">DNI</label>
                <input className="input" value={form.dni || ""} onChange={(e) => setForm({ ...form, dni: e.target.value })}/></div>
              <div><label className="label">Teléfono</label>
                <input className="input" value={form.telefono || ""} onChange={(e) => setForm({ ...form, telefono: e.target.value })}/></div>
              <div><label className="label">Email</label>
                <input className="input" type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })}/></div>
              <div><label className="label">Fecha reconocimiento médico</label>
                <DatePicker value={form.fechaReconocimientoMedico || ""} onChange={(v) => setForm({ ...form, fechaReconocimientoMedico: v })} testid="personal-fecha-medico"/></div>
              <div><label className="label">Fecha caducidad PRL</label>
                <DatePicker value={form.fechaCaducidadPRL || ""} onChange={(v) => setForm({ ...form, fechaCaducidadPRL: v })} testid="personal-fecha-prl"/></div>
              <div><label className="label">Vehículo</label>
                <input className="input" value={form.vehiculo || ""} onChange={(e) => setForm({ ...form, vehiculo: e.target.value })}/></div>
              <div><label className="label">Matrícula</label>
                <input className="input" value={form.matricula || ""} onChange={(e) => setForm({ ...form, matricula: e.target.value })}/></div>
              <div><label className="label">Etiqueta medioambiental</label>
                <select className="select" value={form.etiquetaAmbiental || ""} onChange={(e) => setForm({ ...form, etiquetaAmbiental: e.target.value })}>
                  {ETIQUETAS.map((t) => <option key={t} value={t}>{t || "—"}</option>)}
                </select>
              </div>
              <div><label className="label">Herramienta prestada</label>
                <input className="input" value={form.herramientaPrestada || ""} onChange={(e) => setForm({ ...form, herramientaPrestada: e.target.value })} placeholder="Ej: Taladro Bosch..."/></div>
              <div className="md:col-span-2"><label className="label">Acuerdos con la empresa</label>
                <textarea className="textarea" rows="3" value={form.acuerdos || ""} onChange={(e) => setForm({ ...form, acuerdos: e.target.value })}/></div>
              <div className="md:col-span-2 flex justify-end gap-2 pt-2">
                <button type="button" className="btn btn-ghost" onClick={() => setModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" data-testid="personal-save">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
